import { Controller } from "@hotwired/stimulus";
import Combobox from "@github/combobox-nav";
import { useClickOutside } from "stimulus-use";

export default class extends Controller {
  static targets = ["input", "hidden", "list", "option", "checkbox"];
  static values = {
    multiple: {
      type: Boolean,
      default: false,
    },
  };

  connect() {
    useClickOutside(this);

    this.combobox = new Combobox(this.inputTarget, this.listTarget);
    this.arrowMapping = new Map([
      ["ArrowDown", 1],
      ["ArrowUp", -1],
    ]);

    this.selectedOptionValues = new Set();
  }

  disconnect() {
    this.combobox.destroy();
  }

  show() {
    this.listTarget.classList.remove("hidden");
    this.listTarget.hidden = false;
    this.combobox.start();
  }

  hide() {
    this.listTarget.classList.add("hidden");
    this.listTarget.hidden = true;
    this.combobox.clearSelection();
    this.combobox.stop();
  }

  search() {
    const query = this.inputTarget.value;
    this.optionTargets.forEach(
      (option) => (option.hidden = !this.isMatch(option, query)),
    );

    const filteredCount = this.optionTargets.filter(
      (item) => !item.hidden,
    ).length;
    if (filteredCount > 0) {
      this.show();
    } else {
      this.hide();
    }
  }

  isMatch(item, query) {
    return item.textContent.toLowerCase().includes(query.toLowerCase());
  }

  // Only triggers search on closed list, doesn't handle traversing open list
  arrowSearch(event) {
    if (!this.arrowMapping.has(event.key)) return;
    if (!this.listTarget.hidden) return;

    this.search();
    if (this.listTarget.hidden) return;

    this.combobox.navigate(this.arrowMapping.get(event.key));
  }

  // Does not work for clicks outside controller (no relatedTarget set),
  // but this handles tabbing away and useClickOutside handles clicks.
  blur(event) {
    if (this.listTarget.hidden) return;

    if (!event.relatedTarget) return;
    if (this.element.contains(event.relatedTarget)) return;

    this.hide();
  }

  clickOutside(event) {
    if (this.listTarget.hidden) return;
    this.hide();
  }

  commit(event) {
    const option = event.target.closest('[role="option"]');
    const optionText = option.textContent.trim();
    const optionValue = option.dataset["essence-AutocompleteOptionValueParam"];

    if (this.multipleValue) {
      const checkbox = option.querySelector('input[type="checkbox"]');
      this.updateSelectedValuesState(optionValue);
      checkbox.checked = this.selectedOptionValues.has(optionValue);

      this.updateInputWithSelectedValues();
    } else {
      this.inputTarget.value = optionText;
      this.hiddenTarget.value = optionValue;
      this.hide();
    }
    console.log(this.selectedOptionValues);
  }

  updateSelectedValuesState(value) {
    const state = this.selectedOptionValues;
    state.has(value) ? state.delete(value) : state.add(value);
  }

  updateInputWithSelectedValues() {
    const selectedTexts = Array.from(this.selectedOptionValues).map((value) => {
      const option = this.optionTargets.find(
        (opt) => opt.dataset["essence-AutocompleteOptionValueParam"] === value,
      );
      return option ? option.textContent.trim() : value;
    });

    this.inputTarget.value = selectedTexts.join(", ");
    this.hiddenTarget.value = Array.from(this.selectedOptionValues).join(",");
  }
}
